// src/components/Pagination.jsx
// Reusable pagination bar. Shows prev/next + up to 7 page numbers with ellipsis.
// Props:
//   page    — current page (1-based)
//   pages   — total pages
//   total   — total records (for "Showing X–Y of Z" label)
//   limit   — records per page
//   onChange(newPage) — called when user clicks a page
import React from 'react';

const GREEN = '#2e9f86';

export default function Pagination({ page, pages, total, limit, onChange }) {
  if (!pages || pages <= 1) return null;

  // Build page number list with ellipsis
  const getPageNumbers = () => {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    const nums = [];
    if (page <= 4) {
      nums.push(1, 2, 3, 4, 5, '…', pages);
    } else if (page >= pages - 3) {
      nums.push(1, '…', pages - 4, pages - 3, pages - 2, pages - 1, pages);
    } else {
      nums.push(1, '…', page - 1, page, page + 1, '…', pages);
    }
    return nums;
  };

  const from = Math.min((page - 1) * limit + 1, total);
  const to   = Math.min(page * limit, total);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-1 py-3 border-t mt-2">
      {/* Record count label */}
      <span className="text-xs text-gray-500 whitespace-nowrap">
        Showing {from}–{to} of {total.toLocaleString()}
      </span>

      {/* Page buttons */}
      <div className="flex items-center gap-1 flex-wrap justify-center">
        {/* Prev */}
        <button
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
          className="px-2.5 py-1.5 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          ‹
        </button>

        {getPageNumbers().map((n, i) =>
          n === '…' ? (
            <span key={`ellipsis-${i}`} className="px-1 text-gray-400 text-sm select-none">…</span>
          ) : (
            <button
              key={n}
              onClick={() => onChange(n)}
              className="w-8 h-8 text-sm rounded border font-medium transition"
              style={n === page
                ? { backgroundColor: GREEN, borderColor: GREEN, color: '#fff' }
                : { borderColor: '#d1d5db', color: '#374151', background: '#fff' }
              }
            >
              {n}
            </button>
          )
        )}

        {/* Next */}
        <button
          onClick={() => onChange(page + 1)}
          disabled={page >= pages}
          className="px-2.5 py-1.5 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          ›
        </button>
      </div>
    </div>
  );
}
